<crowdctrl-unauthenticated-page [pageTitle]="pageTitle" [showFooter]="false" defaultHref="/about">
  <div class="about-technical">
    <p class="about-technical__lede">What runs in the browser. What runs in the cloud.</p>

    <section class="about-technical__section" aria-labelledby="tech-stack-heading">
      <h2 id="tech-stack-heading" class="about-technical__heading">Our technology stack</h2>
      <p class="about-technical__section-note">
        We run web apps in the browser, native shells where native access is required, and HTTPS
        APIs on managed cloud. Each section below describes one part of the stack. We update this
        page when something material changes.
      </p>
      <div class="about-technical__card-grid" role="list" aria-labelledby="tech-stack-heading">
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <ion-icon
              class="about-technical__card-icon"
              name="logo-angular"
              aria-hidden="true"
            ></ion-icon>
            <h3 class="about-technical__card-title">Web apps</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              CrowdCTRL ships several Ionic Angular web apps, with TypeScript for most of the logic,
              HTML for templates, and CSS for layout and styling.
            </p>
            <p>
              Where it fits, each is delivered as a progressive web app (PWA): people add it from
              the browser, and new builds reach them when we deploy to the host.
            </p>
          </div>
        </article>
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <ion-icon
              class="about-technical__card-icon"
              name="logo-capacitor"
              aria-hidden="true"
            ></ion-icon>
            <h3 class="about-technical__card-title">Capacitor</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              Those web UIs are wrapped in native Android shells with Capacitor, using plugins for
              Google and Microsoft sign-in, Near Field Communication (NFC), and Brother label
              printing where native access matters.
            </p>
            <p>
              iOS is not out yet. We know exactly what it costs. We also know what the bank account
              said when we asked.
            </p>
          </div>
        </article>
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <ion-icon
              class="about-technical__card-icon"
              name="logo-python"
              aria-hidden="true"
            ></ion-icon>
            <h3 class="about-technical__card-title">Backend</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              Firebase Cloud Functions (Python, 2nd generation) expose HTTPS APIs for anything that
              should not be done as a browser-only trick.
            </p>
            <p>
              They run in Melbourne, Australia (Google Cloud region australia-southeast2), require a
              signed-in user, and use Firebase App Check so traffic is expected to come from real
              clients. Regional hosting keeps API latency down for the geography we target.
            </p>
          </div>
        </article>
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <ion-icon
              class="about-technical__card-icon"
              name="logo-firebase"
              aria-hidden="true"
            ></ion-icon>
            <h3 class="about-technical__card-title">Platform</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              Firebase Authentication, including Google and Microsoft sign-in; Cloud Firestore is
              the database for structured data; Firebase Hosting for the web apps; and Firebase
              Storage for uploads, downloads, and other binary payloads we do not store in the
              database.
            </p>
            <p>
              Google Cloud services back queued work and background jobs that should not block a
              single HTTP request.
            </p>
          </div>
        </article>
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <div class="about-technical__card-icon-wrap" aria-hidden="true">
              <ion-icon class="about-technical__card-icon" name="card-outline"></ion-icon>
            </div>
            <h3 class="about-technical__card-title">Stripe</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              Stripe is the planned stack for when we start taking money: subscriptions and other
              billing shapes we have not turned on yet.
            </p>
            <p>
              Today everything is effectively pre-revenue. One day we will monetise properly; until
              then this box is mostly a promise to future accountants.
            </p>
          </div>
        </article>
      </div>
    </section>

    <section class="about-technical__section" aria-labelledby="security-scalability-heading">
      <h2 id="security-scalability-heading" class="about-technical__heading">
        Security and Scalability
      </h2>
      <p class="about-technical__section-note">
        Many classes at once means many sessions at once, on cloud infrastructure that scales with
        traffic. The sections below give a high-level read on how we approach security and
        scalability: the sort of thing customers and partners usually ask about first.
      </p>
      <div
        class="about-technical__card-grid"
        role="list"
        aria-labelledby="security-scalability-heading"
      >
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <ion-icon
              class="about-technical__card-icon"
              name="shield-checkmark-outline"
              aria-hidden="true"
            ></ion-icon>
            <h3 class="about-technical__card-title">Security</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              Authentication and tenancy are non-negotiable: APIs expect signed-in users, and data
              access lines up with organisation membership and roles. Firebase App Check is part of
              the story so traffic is expected to come from real clients, and not random scripts.
            </p>
            <p>
              We think in layers. Google Cloud Armor is how we put the same edge protection in front
              of every public entry point, together with App Check and authentication.
            </p>
          </div>
        </article>
        <article class="about-technical__card" role="listitem">
          <div class="about-technical__card-header">
            <ion-icon
              class="about-technical__card-icon"
              name="trending-up-outline"
              aria-hidden="true"
            ></ion-icon>
            <h3 class="about-technical__card-title">Scalability</h3>
          </div>
          <div class="about-technical__card-body">
            <p>
              The product is built for busy periods and growth: managed cloud, regional hosting, and
              keeping slow or heavy work off the request path the UI is waiting on, using async and
              background processing where that makes sense. The aim is to stay responsive as load
              goes up.
            </p>
          </div>
        </article>
      </div>
    </section>
  </div>
</crowdctrl-unauthenticated-page>
